import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { onViewChange } from "@/resources/assets/js/modules/utils";

function Image({ location, activeLocation }) {
  return (
    <img
      className={location === activeLocation ? "isActive" : ""}
      src={`${globals.themeRoot}/resources/assets/img/house-${location}.webp`}
      alt={`House ${location} image`}
    />
  );
}

function Images({ locations, activeLocation }) {
  return locations.map((location, index) => (
    <Image key={index} location={location} activeLocation={activeLocation} />
  ));
}

function Button({ linkToPage, pageLink, link, text, newTab }) {
  return (
    <a
      href={linkToPage ? pageLink : link}
      className="Button"
      aria-label={`Click this button to go to the ${linkToPage ? pageLink.replace("-", " ") : link.replace("-", " ")} page`}
      {...(newTab ? ' target="_blank" rel="noopener noreferrer"' : "")}
    >
      {text}
    </a>
  );
}

function Buttons({ buttons }) {
  return buttons.map((button, index) => <Button key={index} {...button} />);
}

function Alarm({ type, title, description, buttons }) {
  return (
    <div className="Card-alarm">
      <h4>
        <span className={type.toLowerCase().replace(" ", "-")}></span>
        {title}
      </h4>
      <p>{description}</p>

      <Buttons buttons={buttons} />
    </div>
  );
}

function Alarms({ alarms }) {
  return alarms.map((alarm, index) => <Alarm key={index} {...alarm} />);
}

function InfoCard({ card, location, activeLocation }) {
  const html_description = { __html: card.description };
  return (
    <div
      className={`Card ${location} ${location === activeLocation ? "isActive" : ""}`}
    >
      <h3>{card.title}</h3>
      <p dangerouslySetInnerHTML={html_description}></p>

      {location === "default" && (
        <>
          <p className="info">
            <i className="fa-regular fa-arrow-pointer"></i>
            Click on a room to discover the recommended products to protect you.
          </p>

          <a
            href={card.button.link}
            className="Button"
            aria-label={`Click this button to go to the ${card.button.link}`}
          >
            {card.button.text}
          </a>
        </>
      )}

      {card.alarms && <Alarms alarms={card.alarms} />}
    </div>
  );
}

function InfoCards({ cards, locations, activeLocation }) {
  return cards.map((card, index) => (
    <InfoCard
      key={index}
      card={card}
      location={locations[index]}
      activeLocation={activeLocation}
    />
  ));
}

function Overlay({ handleClick }) {
  return (
    <div className="InteractiveHouse-overlay">
      <span
        data-location="garage"
        aria-label="Click to check garage information"
        onClick={handleClick}
      ></span>
      <span
        data-location="lounge"
        aria-label="Click to check lounge information"
        onClick={handleClick}
      ></span>
      <span
        data-location="kitchen"
        aria-label="Click to check kitchen information"
        onClick={handleClick}
      ></span>
      <span
        data-location="bedroom"
        aria-label="Click to check bedroom information"
        onClick={handleClick}
      ></span>
      <span
        data-location="hallway"
        aria-label="Click to check hallway information"
        onClick={handleClick}
      ></span>
      <span
        data-location="loft"
        aria-label="Click to check loft information"
        onClick={handleClick}
      ></span>
    </div>
  );
}

function Dropdown({
  locations,
  activeLocation,
  roomsTitles,
  handleClick,
  handleDropdownVisibility,
}) {
  const filteredLocations = locations.filter(
    (location) => location !== "default"
  );
  const activeLocationIndex = locations.indexOf(activeLocation) - 1;

  return (
    <>
      <p onClick={handleDropdownVisibility}>
        <span>
          {activeLocation === "default"
            ? "Default"
            : roomsTitles[activeLocationIndex]}
        </span>
        <i className="fa-sharp fa-regular fa-chevron-down"></i>
      </p>
      <ul>
        {roomsTitles.map((room, index) => (
          <li
            className={
              activeLocation === filteredLocations[index] ? "isActive" : ""
            }
            key={index}
            data-location={filteredLocations[index]}
            aria-label={`Click to check ${room} information`}
            onClick={handleClick}
          >
            {room}
          </li>
        ))}
      </ul>
    </>
  );
}

function InteractiveHouse() {
  const locations = Object.keys(globals.house);
  const [activeLocation, setActiveLocation] = useState("default");
  const roomsTitles = Object.values(globals.house)
    .slice(1)
    .map((room) => room.title);

  function resizeImagesContainer() {
    setTimeout(() => {
      const imagesContainer = document.querySelector(
        ".InteractiveHouse-images"
      );
      imagesContainer.style.height = `${imagesContainer.querySelector("img").clientHeight}px`;
    }, 200);
  }

  function resizeCardsContainer(location = "default") {
    const cardsContainer = document.querySelector(".InteractiveHouse-cards");

    if (window.innerWidth < 992) {
      cardsContainer.style.height = `${cardsContainer.querySelector(`.Card.${location}`).clientHeight}px`;
    }
  }

  function handleClick(e) {
    const location = e.target.dataset.location;
    const dropdown = document.querySelector(".InteractiveHouse-dropdown ul");
    const dropdownP = document.querySelector(".InteractiveHouse-dropdown p");

    dropdown.classList.remove("isVisible");
    dropdownP.classList.remove("isActive");

    resizeCardsContainer(location);
    setActiveLocation(location);
  }

  function handleDropdownVisibility(e) {
    const dropdown = document.querySelector(".InteractiveHouse-dropdown ul");

    e.target.closest("p").classList.toggle("isActive");
    dropdown.classList.toggle("isVisible");
  }

  useEffect(() => {
    resizeCardsContainer();
    resizeImagesContainer();
  }, []);

  onViewChange(resizeImagesContainer);

  return (
    <>
      <div className="InteractiveHouse-cards">
        <InfoCards
          cards={Object.values(globals.house)}
          locations={locations}
          activeLocation={activeLocation}
        />
      </div>

      <div className="InteractiveHouse-dropdown">
        <Dropdown
          locations={locations}
          roomsTitles={roomsTitles}
          activeLocation={activeLocation}
          handleClick={handleClick}
          handleDropdownVisibility={handleDropdownVisibility}
        />
      </div>

      <div className="InteractiveHouse-images">
        <Images locations={locations} activeLocation={activeLocation} />
        <Overlay handleClick={handleClick} />
      </div>
    </>
  );
}

[...document.querySelectorAll(".InteractiveHouse")].forEach((el) => {
  const root = ReactDOM.createRoot(document.getElementById("InteractiveHouse"));
  root.render(<InteractiveHouse />);
});
